/*
 * quick-order-catalog.js
 * -----------------------------------------------------------------------------
 * Drives the Quick Order page as a thin UI over Shopify's real cart.
 *
 * Quantity uses Dawn's native <quantity-input> element (defined in global.js):
 * the +/- buttons update the number instantly and locally (stepUp/stepDown) and
 * dispatch a bubbling `change` event. <quantity-input> makes NO cart calls
 * itself; the commit is entirely up to us. We listen for that `change` and
 * commit it DEBOUNCED (300ms), so rapid clicks update the number with zero lag
 * and fire exactly one cart write once clicking stops.
 *
 * Mutation routing:
 *   - Variant NOT yet in cart, target > 0 -> /cart/add.js   (creates the line)
 *   - Variant already in cart             -> /cart/change.js by line KEY
 *                                            (SETS absolute qty; 0 removes)
 *
 * Sync surfaces (for PAGE-originated changes):
 *   - Page subtotal + line totals -> read from the cart response here.
 *   - Cart DRAWER contents        -> we repaint the drawer's `.drawer__inner`
 *                                    OURSELVES from a fresh `?section_id=cart-drawer`
 *                                    render, WITHOUT calling Dawn's open(). The
 *                                    Quick Order page must never open the drawer,
 *                                    which rules out renderContents() (it always
 *                                    calls open() and crashed on trapFocus).
 *                                    We repaint `.drawer__inner` (class + inner
 *                                    HTML) AND sync the `is-empty` class on the
 *                                    <cart-drawer> element itself -- Dawn's CSS and
 *                                    its open()/trapFocus logic key off that class,
 *                                    and if it goes stale the freshly-painted items
 *                                    are hidden (blank drawer) and open() crashes.
 *   - Header cart BUBBLE count    -> swapped here from the cart-icon-bubble section.
 *
 *   - Drawer qty -> 0 (native)    -> in the CART DRAWER (Dawn's native control,
 *                                    NOT this page's debounced input), a quantity
 *                                    of 0 is routed to Dawn's native line-remove
 *                                    -- the same path as the trash icon -- instead
 *                                    of Dawn's "minimum of 1" reject followed by a
 *                                    resetQuantityInput crash. See
 *                                    attachDrawerZeroRemoval().
 *
 * We do NOT publish PUB_SUB_EVENTS.cartUpdate for our own changes: doing so made
 * Dawn's cart-drawer subscriber race our repaint over the same nodes. Instead we
 * own the drawer repaint for page-originated changes. We DO still SUBSCRIBE to
 * cartUpdate so the page reflects changes made FROM the drawer (reverse sync);
 * those drawer-originated changes are repainted by Dawn itself, as normal.
 *
 * Totals are always re-read from Shopify (never computed locally). Requests are
 * serialized so commits can't land out of order.
 */
(function () {
  const COMMIT_DEBOUNCE_MS = 300;

  class QuickOrderCatalog extends HTMLElement {
    constructor() {
      super();
      this.queue = Promise.resolve();
      this.rows = new Map();      // String(variantId) -> { input, lineTotal }
      this.lineKeys = new Map();  // String(variantId) -> cart line item key
      this.activeInput = null;
    }

    connectedCallback() {
      this.subtotalEl = this.querySelector('[data-subtotal]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      const debounceFn = typeof debounce === 'function'
        ? debounce
        : (fn, wait) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };

      this.querySelectorAll('[data-qo-row]').forEach((row) => {
        const variantId = String(row.dataset.variantId);
        const input = row.querySelector('[data-qty-input]');
        const lineTotal = row.querySelector('[data-line-total]');
        if (!input) return;
        this.rows.set(variantId, { input, lineTotal });

        const commit = debounceFn(() => {
          this.activeInput = null;
          this.setQuantity(variantId, this.readQty(input));
        }, COMMIT_DEBOUNCE_MS);

        input.addEventListener('focus', () => { this.activeInput = input; });
        input.addEventListener('change', () => {
          this.activeInput = input;
          commit();
        });

        const removeButton = row.querySelector('[data-qo-remove]');
        if (removeButton) {
          removeButton.addEventListener('click', () => {
            this.activeInput = null;
            input.value = 0;
            this.setQuantity(variantId, 0);
          });
        }
      });

      // Initial load: sync the PAGE inputs + line-key map from the live cart.
      // The drawer and bubble are already server-rendered correctly on load, so
      // we deliberately do NOT repaint them here.
      this.enqueue(() => this.fetchCart().then((cart) => this.paintCart(cart)));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'quick-order-catalog') return;
          if (this.activeInput) return;
          this.enqueue(() => this.fetchCart().then((cart) => this.renderFromExternal(cart)));
        });
      }

      // In the cart drawer, make a quantity of 0 remove the line (native remove)
      // instead of tripping Dawn's min-of-1 reject + crash.
      this.attachDrawerZeroRemoval();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this.onDrawerQtyChange) document.removeEventListener('change', this.onDrawerQtyChange, true);
    }

    // Make the CART DRAWER treat a quantity of 0 as "remove this line".
    //
    // The drawer's quantity control is Dawn's NATIVE <quantity-input> + cart.js,
    // NOT this page's debounced input -- so this has nothing to do with our
    // commit debounce. Dawn enforces the variant's quantity rule (min 1) there:
    // stepping/typing to 0 shows "This item has a minimum of 1" and then throws
    // in resetQuantityInput. We intercept the drawer input's `change` in the
    // CAPTURE phase (so we run BEFORE Dawn's own handler and can cancel it), and
    // when the value is 0 we trigger Dawn's native remove -- the exact same path
    // as the trash icon. Delegated on `document`, so it survives every drawer
    // re-render; scoped to the drawer (this page's own inputs are ignored); and
    // because this script only loads on the Quick Order template, the drawer
    // stays 100% native on every other page.
    attachDrawerZeroRemoval() {
      this.onDrawerQtyChange = (event) => {
        const input = event.target;
        if (!input || !input.classList || !input.classList.contains('quantity__input')) return;
        if (!input.closest('cart-drawer')) return;            // drawer inputs only
        const value = parseInt(input.value, 10);
        if (isNaN(value) || value > 0) return;                // only act on 0

        // Stop Dawn from validating (the "minimum of 1" bubble + the crash).
        event.stopImmediatePropagation();

        // Remove the line the native way -- same as clicking the trash icon.
        const line = input.closest('.cart-item')
          || input.closest('tr')
          || input.closest('[id^="CartDrawer-Item"]');
        const removeButton = line && line.querySelector('cart-remove-button');
        if (removeButton) {
          removeButton.click();
          return;
        }

        // Fallback: call Dawn's native remove directly.
        const cartItems = input.closest('cart-drawer-items');
        const index = input.dataset.index;
        if (cartItems && index && typeof cartItems.updateQuantity === 'function') {
          cartItems.updateQuantity(index, 0);
        }
      };
      document.addEventListener('change', this.onDrawerQtyChange, true); // capture
    }

    readQty(input) {
      const n = parseInt(input.value, 10);
      return isNaN(n) || n < 0 ? 0 : n;
    }

    enqueue(task) {
      this.queue = this.queue.then(task).catch((err) => {
        console.error('Quick Order cart error:', err);
      });
      return this.queue;
    }

    setQuantity(variantId, target) {
      if (target < 0) target = 0;

      this.enqueue(() => {
        const existingKey = this.lineKeys.get(variantId);
        let request;

        if (existingKey) {
          request = this.changeByKey(existingKey, target);
        } else if (target > 0) {
          request = this.addVariant(variantId, target).then(() => this.fetchCart());
        } else {
          request = this.fetchCart();
        }

        return request.then((cart) => this.render(cart));
      });
    }

    fetchCart() {
      return fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json());
    }

    addVariant(variantId, quantity) {
      return fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(variantId), quantity: quantity }),
      }).then((r) => {
        if (!r.ok) return r.json().then((e) => { throw new Error(e.description || 'add failed'); });
        return r.json();
      });
    }

    changeByKey(lineKey, quantity) {
      return fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity }),
      }).then((r) => r.json());
    }

    paintCart(cart) {
      this.lineKeys.clear();
      const byVariant = {};
      (cart.items || []).forEach((item) => {
        const vid = String(item.variant_id);
        byVariant[vid] = item;
        this.lineKeys.set(vid, item.key);
      });

      this.rows.forEach((entry, variantId) => {
        const item = byVariant[variantId];
        const qty = item ? item.quantity : 0;
        const linePrice = item ? item.final_line_price : 0;
        if (entry.input !== this.activeInput) entry.input.value = qty;
        if (entry.lineTotal) entry.lineTotal.textContent = this.formatMoney(linePrice);
      });

      if (this.subtotalEl) {
        this.subtotalEl.textContent = this.formatMoney(cart.items_subtotal_price || 0);
      }
    }

    // Page-originated render: update the page, the bubble, and the drawer.
    render(cart) {
      this.paintCart(cart);
      this.updateCartBubble(cart);
      return this.paintDrawer();
    }

    // Drawer-originated (reverse sync): only repaint the page. Dawn already
    // repainted the drawer itself, so we must not fight it here.
    renderFromExternal(cart) {
      this.paintCart(cart);
    }

    // Repaint the cart drawer to match the current cart WITHOUT opening it.
    //
    // Dawn keeps the drawer's empty state in TWO places: an `is-empty` class on
    // the <cart-drawer> element itself (which its CSS and its open()/trapFocus
    // logic read), and the actual markup inside `.drawer__inner` (item list +
    // footer, or the "cart is empty" block). Earlier versions only repainted
    // `.drawer__inner`; the <cart-drawer> class went stale. That stale class is
    // what made the drawer render blank after a page-add (CSS hid the freshly
    // painted items) and crash open()'s trapFocus (it looked for a
    // .drawer__inner-empty that no longer existed) -- even though every probe of
    // `.drawer__inner`/`cart-drawer-items` looked perfectly healthy.
    //
    // So we fetch the freshly rendered section and (1) swap `.drawer__inner`'s
    // class + inner HTML, then (2) sync the `is-empty` class on <cart-drawer>
    // itself -- toggling ONLY that one class so we never clobber the runtime
    // open/close classes (active/animate) and an open drawer stays open. We
    // never call open()/renderContents; the page must not open the drawer.
    paintDrawer() {
      const drawer = document.querySelector('cart-drawer');
      const drawerInner = (drawer || document).querySelector('.drawer__inner');
      if (!drawerInner) return Promise.resolve();

      return fetch(`${this.root}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const freshDrawer = doc.querySelector('cart-drawer');
          const freshInner = doc.querySelector('.drawer__inner');
          if (!freshInner) return;

          // Empty-state per the fresh server render.
          const isEmpty = freshDrawer
            ? freshDrawer.classList.contains('is-empty')
            : !!doc.querySelector('.drawer__inner-empty');

          // (1) Repaint the drawer body: item list + footer, or empty message.
          drawerInner.className = freshInner.className;
          drawerInner.innerHTML = freshInner.innerHTML;

          // (2) Sync ONLY the empty-state flag on <cart-drawer>, preserving the
          // active/animate classes so an open drawer stays open.
          if (drawer) drawer.classList.toggle('is-empty', isEmpty);
        })
        .catch(() => {});
    }

    updateCartBubble(cart) {
      const target = document.getElementById('cart-icon-bubble');
      if (!target) return;

      const swap = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.shopify-section');
        if (inner) target.innerHTML = inner.innerHTML;
      };

      if (cart.sections && cart.sections['cart-icon-bubble']) {
        swap(cart.sections['cart-icon-bubble']);
      } else {
        fetch(`${this.root}?section_id=cart-icon-bubble`)
          .then((r) => r.text())
          .then(swap)
          .catch(() => {});
      }
    }

    formatMoney(cents) {
      const value = (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value);
    }
  }

  if (!customElements.get('quick-order-catalog')) {
    customElements.define('quick-order-catalog', QuickOrderCatalog);
  }
})();